import { getCategoryForValue, Ranges } from '@deighton/shared-ui/core';
import type {
  DataGridColumn,
  DataGridGroupNode,
  DataGridGroupSortState,
  SortDirection
} from './data-grid.types';
import { isDomainValueObject } from './domain-value.utils';

/** Delimiter used to build composite path keys for nested groups. */
export const DATA_GRID_GROUP_PATH_DELIMITER: string = '///';

/** CDK drop-list id for the group bar; theads connect to this string across components. */
export const DATA_GRID_GROUP_BAR_DROP_LIST_ID: string = 'group-bar-drop-list';

/** Display key used when a grouped cell value cannot be resolved. */
export const DATA_GRID_UNKNOWN_GROUP_KEY: string = 'Unknown';

/**
 * Canonicalises a discriminator group key to its display name when a label exists.
 *
 * @param value - Slug or display name from a row or summary.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Display name, or the original value when unmapped.
 */
export function canonicalDiscriminatorGroupKey(
  value: string,
  discriminatorLabels: Record<string, string>
): string {
  return discriminatorLabels[value] ?? value;
}

/**
 * Resolves a primary header total, preferring slug-keyed map-scoped counts.
 *
 * Display-name group headers (e.g. `Pavement`) must not use a later alias
 * pagination entry whose `totalCount` is unscoped.
 *
 * @param groupKey - Canonical group header key (slug or display name).
 * @param typeTotalCounts - Primary pagination totals keyed by slug and/or label.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Matching total, or undefined when none is present.
 */
export function resolveTypeTotalCount(
  groupKey: string,
  typeTotalCounts: Record<string, number>,
  discriminatorLabels: Record<string, string>
): number | undefined {
  for (const [slug, label] of Object.entries(discriminatorLabels)) {
    if (label !== groupKey || slug === groupKey) {
      continue;
    }
    const slugCount: number | undefined = typeTotalCounts[slug];
    if (slugCount !== undefined) {
      return slugCount;
    }
  }
  return typeTotalCounts[groupKey];
}

/** Delimiter joining ancestor values in `subGroupCounts` keys. */
export const DATA_GRID_GROUP_KEY_DELIMITER: string = ':::';

/** Upper bound on alternative spellings considered for a single count key. */
const MAX_COUNT_KEY_SPELLINGS: number = 32;

/**
 * Returns the slugs a discriminator display name stands for, excluding the name itself.
 *
 * @param value - Display name, or any segment that may be one.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Slugs sharing that label.
 */
function discriminatorSlugsFor(value: string, discriminatorLabels: Record<string, string>): Array<string> {
  const slugs: Array<string> = new Array<string>();
  for (const [slug, label] of Object.entries(discriminatorLabels)) {
    if (label === value && slug !== value) {
      slugs.push(slug);
    }
  }
  return slugs;
}

/**
 * Expands a `:::` count key into every spelling its discriminator segments can take.
 *
 * Group headers name the discriminator by display label (`Bridge`) while summary counts keep the
 * backend slug (`bridge_inventory_snbi_primary`), and one label can cover several slugs.
 * Translating only the segment being counted sufficed while the discriminator was the level in
 * question; once it sits mid-chain the mismatch moves into the ancestor prefix, so a lookup for
 * `00000:::Bridge:::Active` misses the stored `00000:::bridge_..._primary:::Active` and the group
 * falls back to counting the rows it happens to hold.
 *
 * The untranslated spelling comes first, so callers may treat it as the preferred match. The
 * result is capped because a label shared by many slugs multiplies out across segments.
 *
 * @param countKey - Count key in any spelling.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Equivalent keys, original first.
 */
export function expandCountKeySpellings(
  countKey: string,
  discriminatorLabels: Record<string, string>
): Array<string> {
  let spellings: Array<Array<string>> = [new Array<string>()];

  for (const segment of countKey.split(DATA_GRID_GROUP_KEY_DELIMITER)) {
    const alternatives: Array<string> = [segment, ...discriminatorSlugsFor(segment, discriminatorLabels)];
    const next: Array<Array<string>> = new Array<Array<string>>();
    for (const prefix of spellings) {
      for (const alternative of alternatives) {
        next.push([...prefix, alternative]);
      }
    }
    spellings = next.slice(0, MAX_COUNT_KEY_SPELLINGS);
  }

  return spellings.map((parts: Array<string>): string => parts.join(DATA_GRID_GROUP_KEY_DELIMITER));
}

/**
 * Resolves a nested header total from server sub-group counts.
 *
 * Both the ancestor prefix and the level's own key are matched against every equivalent spelling,
 * since either can carry a discriminator segment. Counts are summed across matches because
 * several slugs can roll up into one display group.
 *
 * @param parentKey - Composite key of ancestor group values.
 * @param groupKey - Canonical group key for this level.
 * @param isDiscriminatorLevel - Whether this level groups on the discriminator.
 * @param subGroupCounts - Server counts keyed as `"parent:::value"`.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Matching count, or undefined when the server reported none.
 */
export function resolveSubGroupCount(
  parentKey: string,
  groupKey: string,
  isDiscriminatorLevel: boolean,
  subGroupCounts: Record<string, number>,
  discriminatorLabels: Record<string, string>
): number | undefined {
  const directCount: number | undefined =
    subGroupCounts[`${parentKey}${DATA_GRID_GROUP_KEY_DELIMITER}${groupKey}`];
  if (directCount !== undefined) {
    return directCount;
  }

  const groupKeys: Array<string> = isDiscriminatorLevel
    ? [groupKey, ...discriminatorSlugsFor(groupKey, discriminatorLabels)]
    : [groupKey];

  let total: number | undefined;
  const seen: Set<string> = new Set<string>();
  for (const prefix of expandCountKeySpellings(parentKey, discriminatorLabels)) {
    for (const key of groupKeys) {
      const countKey: string = `${prefix}${DATA_GRID_GROUP_KEY_DELIMITER}${key}`;
      if (seen.has(countKey)) {
        continue;
      }
      seen.add(countKey);
      const count: number | undefined = subGroupCounts[countKey];
      if (count !== undefined) {
        total = (total ?? 0) + count;
      }
    }
  }
  return total;
}

/**
 * Server summary maps needed to seed and count nested group buckets.
 */
export interface DataGridGroupBuildContext {
  /** Field → column definition, used for range-based grouping. */
  readonly fieldToColumnMap: Map<string, DataGridColumn>;
  /** Server-provided totals for the primary group field. */
  readonly typeTotalCounts: Record<string, number>;
  /** Server-provided nested counts keyed as `"parent:::value"`. */
  readonly subGroupCounts: Record<string, number>;
  /** Outermost group field used for per-group pagination, or null when ungrouped. */
  readonly primaryGroupField: string | null;
  /** Host discriminator field, or empty when the grid has no type column. */
  readonly discriminatorField: string;
  /** Slug → display name map used to canonicalise discriminator group keys. */
  readonly discriminatorLabels: Record<string, string>;
}

/**
 * Returns true when the group is expanded.
 *
 * Default expansion is by cardinality, not depth: high-cardinality fields start
 * collapsed. Explicit `collapsedGroups` / `expandedGroups` entries override.
 *
 * @param group - The group node to inspect.
 * @param collapsedGroups - Paths the user has explicitly collapsed.
 * @param expandedGroups - Paths the user has explicitly expanded.
 * @param highCardinalityFields - Fields that start collapsed.
 * @returns Whether the group body should render.
 */
export function isGroupExpanded(
  group: DataGridGroupNode,
  collapsedGroups: Set<string>,
  expandedGroups: Set<string>,
  highCardinalityFields: Set<string>
): boolean {
  if (collapsedGroups.has(group.path)) {
    return false;
  }
  if (expandedGroups.has(group.path)) {
    return true;
  }
  return !highCardinalityFields.has(group.field);
}

/**
 * Recursively partitions records into a tree of {@link DataGridGroupNode} nodes.
 *
 * @param records - The records to partition.
 * @param fields - The ordered list of grouping fields.
 * @param depth - Current nesting depth (0 = outermost).
 * @param parentPath - Composite path of ancestor groups.
 * @param parentCountKey - `:::`-joined ancestor key for sub-group summary lookup.
 * @param context - Server summary maps and column metadata.
 * @returns Nested group nodes for the current level.
 */
export function buildNestedGroups(
  records: Array<Record<string, unknown>>,
  fields: Array<string>,
  depth: number,
  parentPath: string,
  parentCountKey: string | undefined,
  context: DataGridGroupBuildContext
): Array<DataGridGroupNode> {
  if (fields.length === 0) {
    return new Array<DataGridGroupNode>();
  }

  const field: string = fields[0];
  const remaining: Array<string> = fields.slice(1);
  const groupMap: Map<string, Array<Record<string, unknown>>> = new Map<
    string,
    Array<Record<string, unknown>>
  >();

  for (const record of records) {
    const rawKey: string = resolveGroupKey(field, record[field], context.fieldToColumnMap);
    const key: string =
      field === context.discriminatorField
        ? canonicalDiscriminatorGroupKey(rawKey, context.discriminatorLabels)
        : rawKey;
    if (!groupMap.has(key)) {
      groupMap.set(key, new Array<Record<string, unknown>>());
    }
    const bucket: Array<Record<string, unknown>> | undefined = groupMap.get(key);
    if (bucket !== undefined) {
      bucket.push(record);
    }
  }

  const summaryChildren: Array<{ value: string; count: number }> = resolveSummaryChildren(
    field,
    parentCountKey,
    context
  );
  const summaryCounts: Map<string, number> = new Map<string, number>();
  for (const child of summaryChildren) {
    const childKey: string =
      field === context.discriminatorField
        ? canonicalDiscriminatorGroupKey(child.value, context.discriminatorLabels)
        : child.value;
    summaryCounts.set(childKey, (summaryCounts.get(childKey) ?? 0) + child.count);
    if (!groupMap.has(childKey)) {
      groupMap.set(childKey, new Array<Record<string, unknown>>());
    }
  }

  if (groupMap.size === 0) {
    return new Array<DataGridGroupNode>();
  }

  const col: DataGridColumn | undefined = context.fieldToColumnMap.get(field);
  const hasRanges: boolean = (col?.colorRanges?.length ?? 0) > 0;

  const entries: Array<[string, Array<Record<string, unknown>>]> = Array.from(groupMap.entries());
  if (hasRanges && col !== undefined) {
    const rangeColumn: DataGridColumn = col;
    entries.sort(
      ([keyA]: [string, unknown], [keyB]: [string, unknown]): number =>
        resolveRangeSortOrder(rangeColumn, keyB) - resolveRangeSortOrder(rangeColumn, keyA)
    );
  } else if (summaryCounts.size > 0) {
    entries.sort(
      (
        [keyA, rowsA]: [string, Array<Record<string, unknown>>],
        [keyB, rowsB]: [string, Array<Record<string, unknown>>]
      ): number => (summaryCounts.get(keyB) ?? rowsB.length) - (summaryCounts.get(keyA) ?? rowsA.length)
    );
  }

  return entries.map(([key, rows]: [string, Array<Record<string, unknown>>]): DataGridGroupNode => {
    const path: string = parentPath ? `${parentPath}${DATA_GRID_GROUP_PATH_DELIMITER}${key}` : key;
    const childCountKey: string = parentCountKey !== undefined ? `${parentCountKey}:::${key}` : key;

    if (remaining.length === 0) {
      const leafTotalCount: number = summaryCounts.get(key) ?? rows.length;
      return {
        field,
        key,
        path,
        depth,
        children: new Array<DataGridGroupNode>(),
        rows,
        totalCount: leafTotalCount
      };
    }

    const children: Array<DataGridGroupNode> = buildNestedGroups(
      rows,
      remaining,
      depth + 1,
      path,
      childCountKey,
      context
    );
    const childTotal: number = children.reduce(
      (sum: number, child: DataGridGroupNode): number => sum + child.totalCount,
      0
    );
    const summaryTotal: number = summaryCounts.get(key) ?? 0;
    const totalCount: number = childTotal > 0 ? childTotal : summaryTotal > 0 ? summaryTotal : rows.length;
    return {
      field,
      key,
      path,
      depth,
      children,
      rows: children.length === 0 ? rows : new Array<Record<string, unknown>>(),
      totalCount
    };
  });
}

/**
 * Returns summary child values used to seed empty group buckets before rows load.
 *
 * @param field - The field being grouped at this level.
 * @param parentCountKey - Ancestor count key, or undefined at the primary level.
 * @param context - Server summary maps.
 * @returns Value/count pairs used to seed missing buckets.
 */
export function resolveSummaryChildren(
  field: string,
  parentCountKey: string | undefined,
  context: DataGridGroupBuildContext
): Array<{ value: string; count: number }> {
  if (parentCountKey !== undefined) {
    return summaryChildValues(parentCountKey, context.subGroupCounts, context.discriminatorLabels);
  }

  const primary: string | null = context.primaryGroupField;
  if (primary === null || field !== primary) {
    return new Array<{ value: string; count: number }>();
  }

  return Object.entries(context.typeTotalCounts).map(
    ([value, count]: [string, number]): { value: string; count: number } => ({ value, count })
  );
}

/**
 * Returns the immediate child group values for a parent count-key from `subGroupCounts`.
 *
 * Every spelling of the ancestor key is searched, so a level nested below the discriminator still
 * finds its children: the stored keys spell that ancestor as a slug, while the caller arrives
 * holding the display name. Without this a third grouping level is seeded only from the rows
 * already loaded, hiding any child value absent from the current page.
 *
 * @param parentCountKey - The `:::`-joined ancestor key.
 * @param subGroupCounts - Nested summary keyed as `"<parent>:::<value>"`.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Direct children of `parentCountKey`.
 */
export function summaryChildValues(
  parentCountKey: string,
  subGroupCounts: Record<string, number>,
  discriminatorLabels: Record<string, string> = {}
): Array<{ value: string; count: number }> {
  const result: Array<{ value: string; count: number }> = [];
  const seen: Set<string> = new Set<string>();

  for (const spelling of expandCountKeySpellings(parentCountKey, discriminatorLabels)) {
    const prefix: string = `${spelling}${DATA_GRID_GROUP_KEY_DELIMITER}`;
    for (const key of Object.keys(subGroupCounts)) {
      if (!key.startsWith(prefix) || seen.has(key)) {
        continue;
      }
      const remainder: string = key.slice(prefix.length);
      if (remainder.length > 0 && !remainder.includes(DATA_GRID_GROUP_KEY_DELIMITER)) {
        seen.add(key);
        result.push({ value: remainder, count: subGroupCounts[key] });
      }
    }
  }
  return result;
}

/**
 * Returns the lower bound of the colorRange whose name matches `key`.
 *
 * @param col - The column definition containing colorRanges.
 * @param key - The resolved group key (category name, e.g. 'Good').
 * @returns Lower bound, or -1 when unmatched so those keys sort last.
 */
export function resolveRangeSortOrder(col: DataGridColumn, key: string): number {
  const range: Ranges | undefined = col.colorRanges?.find(
    (rangeEntry: Ranges): boolean => rangeEntry.name === key
  );
  return range?.range.lowerBound.value ?? -1;
}

/**
 * Resolves the display key for a group bucket.
 *
 * @param field - The field name being grouped.
 * @param value - The raw cell value from the data record.
 * @param fieldToColumnMap - Field → column definition map.
 * @returns Category name, raw string, numeric string, or {@link DATA_GRID_UNKNOWN_GROUP_KEY}.
 */
export function resolveGroupKey(
  field: string,
  value: unknown,
  fieldToColumnMap: Map<string, DataGridColumn>
): string {
  const rawValue: unknown = isDomainValueObject(value) ? value.value : value;
  const col: DataGridColumn | undefined = fieldToColumnMap.get(field);

  if (col?.colorRanges !== undefined && col.colorRanges.length > 0 && typeof rawValue === 'number') {
    const category: Ranges | undefined = getCategoryForValue(rawValue, col.colorRanges);
    return category?.name ?? DATA_GRID_UNKNOWN_GROUP_KEY;
  }

  if (typeof rawValue === 'string') {
    return rawValue;
  }
  if (typeof rawValue === 'number') {
    return String(rawValue);
  }
  return DATA_GRID_UNKNOWN_GROUP_KEY;
}

/**
 * Returns the colour associated with a group key when that field has `colorRanges`.
 *
 * @param field - The field name used for this grouping level.
 * @param key - The resolved group key.
 * @param fieldToColumnMap - Field → column definition map.
 * @returns Range color, or null when none applies.
 */
export function resolveGroupKeyColor(
  field: string,
  key: string,
  fieldToColumnMap: Map<string, DataGridColumn>
): string | null {
  const col: DataGridColumn | undefined = fieldToColumnMap.get(field);
  if (col?.colorRanges === undefined || col.colorRanges.length === 0) {
    return null;
  }
  const range: Ranges | undefined = col.colorRanges.find(
    (rangeEntry: Ranges): boolean => rangeEntry.name === key
  );
  return range?.color ?? null;
}

/**
 * Returns the configured color for an element type, translating slugs via labels.
 *
 * @param typeName - Raw slug or canonical display name.
 * @param discriminatorColors - Color map keyed by display name or slug.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Color string, or null when none is defined.
 */
export function resolveDiscriminatorColor(
  typeName: string,
  discriminatorColors: Record<string, string>,
  discriminatorLabels: Record<string, string>
): string | null {
  if (discriminatorColors[typeName]) {
    return discriminatorColors[typeName];
  }

  const displayName: string | undefined = discriminatorLabels[typeName];
  if (displayName && discriminatorColors[displayName]) {
    return discriminatorColors[displayName];
  }

  return null;
}

/**
 * Returns the `--dg-discriminator-color` value for a group header.
 *
 * @param group - The group node.
 * @param discriminatorField - Host discriminator field, or empty.
 * @param discriminatorColors - Color map for element types.
 * @param discriminatorLabels - Slug → display name map.
 * @param fieldToColumnMap - Field → column definition map.
 * @returns Type color, range color, or null.
 */
export function getGroupHeaderColor(
  group: DataGridGroupNode,
  discriminatorField: string,
  discriminatorColors: Record<string, string>,
  discriminatorLabels: Record<string, string>,
  fieldToColumnMap: Map<string, DataGridColumn>
): string | null {
  const elementTypeColor: string | null =
    group.field === discriminatorField
      ? resolveDiscriminatorColor(group.key, discriminatorColors, discriminatorLabels)
      : null;
  return elementTypeColor ?? resolveGroupKeyColor(group.field, group.key, fieldToColumnMap);
}

/**
 * Returns the display label for a group header, preferring domain `displayValue`.
 *
 * @param group - The nested group node.
 * @returns Human-readable label, or the raw group key.
 */
export function resolveGroupDisplayKey(group: DataGridGroupNode): string {
  if (group.rows.length > 0) {
    const firstRowValue: unknown = group.rows[0][group.field];
    if (isDomainValueObject(firstRowValue) && firstRowValue.displayValue !== null) {
      return firstRowValue.displayValue;
    }
  }
  return group.key;
}

/**
 * Returns the display count for a group header, preferring server summary totals.
 *
 * @param group - The group node.
 * @param parentKey - Ancestor key used for nested summary lookup.
 * @param context - Server summary maps.
 * @returns Count to show in the group header.
 */
export function getGroupDisplayCount(
  group: DataGridGroupNode,
  parentKey: string | undefined,
  context: DataGridGroupBuildContext
): number {
  const primary: string | null = context.primaryGroupField;
  if (primary !== null && group.field === primary && group.depth === 0) {
    const serverCount: number | undefined = resolveTypeTotalCount(
      group.key,
      context.typeTotalCounts,
      context.discriminatorLabels
    );
    if (serverCount !== undefined) {
      return serverCount;
    }
  }

  if (parentKey !== undefined) {
    const subGroupCount: number | undefined = resolveSubGroupCount(
      parentKey,
      group.key,
      context.discriminatorField.length > 0 && group.field === context.discriminatorField,
      context.subGroupCounts,
      context.discriminatorLabels
    );
    if (subGroupCount !== undefined) {
      return subGroupCount;
    }
  }

  if (group.children.length > 0 && primary !== null) {
    const childParentKey: string =
      parentKey !== undefined ? `${parentKey}${DATA_GRID_GROUP_KEY_DELIMITER}${group.key}` : group.key;
    let childSum: number = 0;
    for (const child of group.children) {
      childSum += getGroupDisplayCount(child, childParentKey, context);
    }
    return childSum;
  }

  return group.totalCount;
}

/**
 * Returns true when this node is the last grouping level and should render a leaf table.
 *
 * Intermediate nodes (e.g. Route when grouped Route → Element Type) must not be
 * treated as leaves just because sub-group headers have not been seeded yet.
 *
 * @param group - The group node to inspect.
 * @param groupedFields - Ordered grouping chain.
 * @returns True when the node is a leaf grouping level.
 */
export function isLeafGroupLevel(group: DataGridGroupNode, groupedFields: Array<string>): boolean {
  return groupedFields.length === 0 || group.depth >= groupedFields.length - 1;
}

/**
 * Recursively collects drop-list IDs for all expanded leaf groups.
 *
 * @param groups - Current group nodes to traverse.
 * @param ids - Accumulator array for discovered IDs.
 * @param isExpanded - Predicate for whether a node is expanded.
 * @param groupedFields - Ordered grouping chain used to skip intermediate nodes.
 */
export function collectLeafIds(
  groups: Array<DataGridGroupNode>,
  ids: Array<string>,
  isExpanded: (group: DataGridGroupNode) => boolean,
  groupedFields: Array<string> = new Array<string>()
): void {
  for (const group of groups) {
    if (!isExpanded(group)) {
      continue;
    }
    if (group.children.length > 0) {
      collectLeafIds(group.children, ids, isExpanded, groupedFields);
    } else if (isLeafGroupLevel(group, groupedFields)) {
      ids.push(`thead-${group.path}`);
    }
  }
}

/**
 * Recursively collects composite paths of expanded leaf buckets with no loaded rows.
 *
 * @param groups - The group nodes to traverse.
 * @param acc - Accumulator for the discovered leaf paths.
 * @param isExpanded - Predicate for whether a node is expanded.
 * @param groupedFields - Ordered grouping chain used to load unseeded intermediate groups.
 */
export function collectExpandedUnloadedLeaves(
  groups: Array<DataGridGroupNode>,
  acc: Array<string>,
  isExpanded: (group: DataGridGroupNode) => boolean,
  groupedFields: Array<string> = new Array<string>()
): void {
  for (const group of groups) {
    if (!isExpanded(group)) {
      continue;
    }
    if (group.children.length === 0) {
      if (group.rows.length === 0 && (group.totalCount > 0 || !isLeafGroupLevel(group, groupedFields))) {
        acc.push(group.path);
      }
    } else {
      collectExpandedUnloadedLeaves(group.children, acc, isExpanded, groupedFields);
    }
  }
}

/**
 * Walks a just-expanded group's visible subtree and collects `loadMore` paths.
 *
 * Only leaves holding nothing at all qualify, matching {@link collectUnloadedLeaves}. Expanding is
 * a display action, so it seeds a lazily-collapsed leaf that was never fetched and otherwise
 * leaves pagination to the leaf's own scroll handler. Collecting every leaf short of its total
 * instead turned expand/collapse into a page-advance button: a group sitting at 100 of 305 gained
 * a page on each reopen, without the user scrolling anything.
 *
 * @param group - The group that was just expanded.
 * @param acc - Accumulator for leaf paths that still need rows.
 * @param isExpanded - Predicate for whether a child is expanded.
 * @param groupedFields - Ordered grouping chain used to load unseeded intermediate groups.
 */
export function collectLoadsForExpandedLeaves(
  group: DataGridGroupNode,
  acc: Array<string>,
  isExpanded: (node: DataGridGroupNode) => boolean,
  groupedFields: Array<string> = new Array<string>()
): void {
  if (group.children.length === 0) {
    if (group.rows.length === 0 && (!isLeafGroupLevel(group, groupedFields) || group.totalCount > 0)) {
      acc.push(group.path);
    }
    return;
  }
  for (const child of group.children) {
    if (isExpanded(child)) {
      collectLoadsForExpandedLeaves(child, acc, isExpanded, groupedFields);
    }
  }
}

/**
 * Resolves the data rows for a given group path by traversing the nested tree.
 *
 * @param path - The composite group path.
 * @param groups - Root group nodes.
 * @returns Leaf rows at that path, or an empty array when not found.
 */
export function resolveRowsForPath(
  path: string,
  groups: Array<DataGridGroupNode>
): Array<Record<string, unknown>> {
  const segments: Array<string> = path.split(DATA_GRID_GROUP_PATH_DELIMITER);
  let current: Array<DataGridGroupNode> = groups;
  let target: DataGridGroupNode | undefined;
  for (const segment of segments) {
    target = current.find((group: DataGridGroupNode): boolean => group.key === segment);
    if (target === undefined) {
      return new Array<Record<string, unknown>>();
    }
    current = target.children;
  }
  return target?.rows ?? new Array<Record<string, unknown>>();
}

/**
 * Returns the display-ready cell value from a row for a given column.
 *
 * @param row - The data row.
 * @param field - The column field name.
 * @returns Display value, or null for non-displayable types.
 */
export function getCellValue(row: Record<string, unknown>, field: string): string | number | null {
  const value: unknown = row[field];
  if (isDomainValueObject(value)) {
    return value.displayValue ?? null;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  return null;
}

/**
 * Returns the raw coded value from a row for a given column.
 *
 * @param row - The data row.
 * @param field - The column field name.
 * @returns Coded value for sort/filter/group, or the raw scalar.
 */
export function getCellRawValue(row: Record<string, unknown>, field: string): unknown {
  const value: unknown = row[field];
  if (isDomainValueObject(value)) {
    return value.value;
  }
  return value;
}

/**
 * Resolves the display color for a numeric cell value using the column's color ranges.
 *
 * @param column - The column definition.
 * @param value - The raw cell value.
 * @returns Matching range color, or null.
 */
export function getColorForValue(column: DataGridColumn, value: unknown): string | null {
  const ranges: Array<Ranges> | undefined = column.colorRanges;
  if (ranges === undefined || ranges.length === 0 || typeof value !== 'number') {
    return null;
  }
  const category: Ranges | undefined = getCategoryForValue(value, ranges);
  return category?.color ?? null;
}

/**
 * Advances a sort state through the cycle: none → asc → desc → none.
 *
 * @param current - The current sort state (null = unsorted).
 * @param field - The column field being sorted.
 * @returns Next sort state, or null when cleared.
 */
export function nextSortState(
  current: DataGridGroupSortState | null,
  field: string
): DataGridGroupSortState | null {
  if (current === null || current.field !== field) {
    return { field, dir: 'asc' };
  }
  if (current.dir === 'asc') {
    return { field, dir: 'desc' };
  }
  return null;
}

/**
 * Returns a sorted copy of rows using the given sort state.
 *
 * @param rows - The rows to sort.
 * @param sort - The sort field and direction.
 * @returns A new sorted array.
 */
export function applySort(
  rows: Array<Record<string, unknown>>,
  sort: DataGridGroupSortState
): Array<Record<string, unknown>> {
  return [...rows].sort((rowA: Record<string, unknown>, rowB: Record<string, unknown>): number => {
    const rawA: unknown = getCellRawValue(rowA, sort.field);
    const rawB: unknown = getCellRawValue(rowB, sort.field);
    let comparison: number = 0;
    if (typeof rawA === 'string' && typeof rawB === 'string') {
      comparison = rawA.localeCompare(rawB);
    } else if (typeof rawA === 'number' && typeof rawB === 'number') {
      comparison = rawA - rawB;
    }
    return sort.dir === 'asc' ? comparison : -comparison;
  });
}

/**
 * Canonical element type from the first row of a leaf bucket.
 *
 * @param rows - Leaf rows.
 * @param discriminatorField - Host discriminator field.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Display name or slug, or empty when unknown.
 */
export function resolveLeafElementType(
  rows: Array<Record<string, unknown>>,
  discriminatorField: string,
  discriminatorLabels: Record<string, string>
): string {
  if (rows.length === 0) {
    return '';
  }
  const value: unknown = rows[0][discriminatorField];
  const rawValue: unknown = isDomainValueObject(value) ? value.value : value;
  if (typeof rawValue !== 'string') {
    return '';
  }
  return discriminatorLabels[rawValue] ?? rawValue;
}

/**
 * Resolves the column set for a grouped leaf table.
 *
 * Tries the first row's discriminator, then the group key, then slug/display-name
 * aliases, then shared columns. Empty columns produce blank row bars with no cells.
 *
 * @param group - Leaf group node.
 * @param leafColumnsMap - Columns keyed by element type or `__shared__`.
 * @param hasDifferentiatorInChain - Whether the discriminator is in the group chain.
 * @param discriminatorField - Host discriminator field.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Visible columns for the leaf table.
 */
export function resolveLeafColumns(
  group: DataGridGroupNode,
  leafColumnsMap: Record<string, Array<DataGridColumn>>,
  hasDifferentiatorInChain: boolean,
  discriminatorField: string,
  discriminatorLabels: Record<string, string>
): Array<DataGridColumn> {
  if (!hasDifferentiatorInChain) {
    return leafColumnsMap['__shared__'] ?? new Array<DataGridColumn>();
  }

  const fromRows: string = resolveLeafElementType(group.rows, discriminatorField, discriminatorLabels);
  const fromKey: string = discriminatorLabels[group.key] ?? group.key;
  const candidates: Array<string> = [fromRows, fromKey, group.key];

  for (const candidate of candidates) {
    if (candidate.length > 0 && leafColumnsMap[candidate] !== undefined) {
      return leafColumnsMap[candidate];
    }
  }

  for (const [slug, displayName] of Object.entries(discriminatorLabels)) {
    if ((fromRows === slug || group.key === slug) && leafColumnsMap[displayName] !== undefined) {
      return leafColumnsMap[displayName];
    }
    if ((fromRows === displayName || group.key === displayName) && leafColumnsMap[slug] !== undefined) {
      return leafColumnsMap[slug];
    }
  }

  return leafColumnsMap['__shared__'] ?? new Array<DataGridColumn>();
}

/**
 * Material sort-icon ligature for a sort direction.
 *
 * @param dir - Current direction, or null when unsorted.
 * @returns Icon ligature.
 */
export function sortIconFor(dir: SortDirection | null): string {
  if (dir === 'asc') {
    return 'keyboard_arrow_up';
  }
  if (dir === 'desc') {
    return 'keyboard_arrow_down';
  }
  return 'unfold_more';
}

/**
 * Returns sorted rows when a client-side sort is safe; otherwise the original order.
 *
 * @param rows - Loaded rows.
 * @param sort - Active sort, or null.
 * @param skipClientSort - True when more server pages exist.
 * @returns Rows to render.
 */
export function rowsForSortState(
  rows: Array<Record<string, unknown>>,
  sort: DataGridGroupSortState | null,
  skipClientSort: boolean
): Array<Record<string, unknown>> {
  if (sort === null || skipClientSort) {
    return rows;
  }
  return applySort(rows, sort);
}
